import {
	type AllProductVariants$result,
	AllProductVariantsStore,
	ProductStore,
	ProductVariantStore,
	ProductTitleDataStore
} from '$houdini';

import { error, redirect } from '@sveltejs/kit';
import { mapToNode, removeArchived, transformProductCollectionsToBranch } from '$lib/utils/product';
import slug from 'slug';
import { LOCALE_COUNTRY_PRERENDER_MAPPING, SUPPORTED_LANGUAGES } from '$lib/i18n/constants';
import { resolveRoute } from '$app/paths';
import { addSDS, trimSDS } from '$lib/utils/sds';
import { getVariantIdFromShopifyUri, withRetry } from '$lib/utils';
import { ensureCountry, ensureLocale } from '$lib/utils/i18n';
import { fetchStoryblokContentFor } from '$lib/storyblok/storyblok';
import type { PageLoadEvent } from './$types';

export async function load(event: PageLoadEvent) {
	redirectIfSDS(event);
	await setCanonicalLinks(event);
	const variantSKU = await determineVariantSKU(event);

	const productStore = new ProductStore();
	const { data: productData, errors } = await withRetry(() =>
		productStore.fetch({
			event: event,
			blocking: true,
			variables: {
				handle: trimSDS(event.params.handle),
				country: ensureCountry(event.params.country),
				language: ensureLocale(event.params.locale),
				sdsHandle: addSDS(event.params.handle)
			}
		})
	);

	if (errors) {
		error(500, `${JSON.stringify(errors)}`);
	}
	if (!productData || !productData.product) {
		error(404, `Product not found`);
	}

	const lightVariant = removeArchived(mapToNode(productData.product.variants)).find(
		(node) => node.sku?.value === variantSKU
	);

	if (!lightVariant) {
		const firstSku = productData.product.variants.edges[0].node.sku?.value;
		if (!firstSku) {
			error(500, 'No SKU found for product');
		}
		const currentUrl = new URL(event.url.toString());
		currentUrl.searchParams.set('variantSKU', firstSku || '');
		redirect(307, currentUrl.toString());
	}

	const variantStore = new ProductVariantStore();
	const variantData = await withRetry(() =>
		variantStore.fetch({
			event,
			blocking: true,
			variables: {
				id: lightVariant!.id,
				country: ensureCountry(event.params.country),
				language: ensureLocale(event.params.locale)
			}
		})
	);
	const variant =
		variantData.data?.node?.__typename === 'ProductVariant' ? variantData.data.node : null;
	if (!variant) {
		error(404, `Product variant not found`);
	}

	const fixedProduct = {
		...productData.product,
		variant
	};

	const parentData = await event.parent();

	event.setHeaders({
		'Cache-Control': 'public, max-age=3600'
	});

	return {
		...parentData,
		product: fixedProduct,
		sdsProduct: productData.sdsProduct,
		storyblokData: fetchStoryblokContentFor(
			fixedProduct,
			fixedProduct.variant,
			event.params.locale
		).catch(() => console.error('Failed to fetch storyblok data for product'))
	};
}

function redirectIfSDS(event: PageLoadEvent) {
	if (event.params.handle.endsWith('-sds')) {
		redirect(
			307,
			resolveRoute(`${event.route.id}${event.url.search}`, {
				...event.params,
				handle: trimSDS(event.params.handle)
			})
		);
	}
}

async function setCanonicalLinks(event: PageLoadEvent) {
	const { canonical } = await event.parent();

	const translations = await Promise.all(
		SUPPORTED_LANGUAGES.map(async (locale) => {
			const language = ensureLocale(locale);
			const store = new ProductTitleDataStore();
			const { data, errors } = await store.fetch({
				event,
				variables: { language, handle: event.params.handle },
				blocking: true
			});
			if (errors) {
				console.error(errors);
			}
			return [locale, data];
		})
	);
	const translationMap = Object.fromEntries(translations);

	function hrefForLocale(locale: string, country: string) {
		const parts = [
			'https://www.sprenger.de',
			`${locale}-${country}`,
			transformProductCollectionsToBranch(translationMap[locale].product.collections),
			slug(translationMap[locale].product.title),
			event.params.handle
		];
		return parts.join('/');
	}

	canonical.set([
		{
			rel: 'canonical',
			href: hrefForLocale('de', 'DE')
		},
		...LOCALE_COUNTRY_PRERENDER_MAPPING.filter(
			({ locale, country }) => `${locale}-${country}` !== `de-DE`
		).map(({ locale, country }) => ({
			rel: 'alternate' as const,
			hreflang: `${locale}-${country}`,
			href: hrefForLocale(locale, country)
		}))
	]);
}

async function getProductVariants(event: PageLoadEvent): Promise<AllProductVariants$result> {
	const store = new AllProductVariantsStore();
	const handle = trimSDS(event.params.handle);
	const { data, errors } = await withRetry(() =>
		store.fetch({
			event: event,
			variables: {
				handle,
				sdsHandle: addSDS(handle),
				country: ensureCountry(event.params.country),
				language: ensureLocale(event.params.locale)
			},
			blocking: true
		})
	);

	if (!data || !data.product) {
		error(404, `Product not found`);
	}

	if (errors) {
		error(500, `${JSON.stringify(errors)}`);
	}
	return data;
}

async function determineVariantSKU(event: PageLoadEvent): Promise<string> {
	let variantSKU = event.url.searchParams.get('variantSKU');
	if (!variantSKU) {
		const productVariants = await getProductVariants(event);
		const variantId =
			event.url.searchParams.get('variant') || event.url.searchParams.get('variantId');
		if (variantId) {
			const variant = [
				...mapToNode(productVariants.product?.variants),
				...mapToNode(productVariants.sdsProduct?.variants)
			].find((node) => getVariantIdFromShopifyUri(node.id) === variantId);
			variantSKU = variant?.sku?.value || null;
		}
		if (!variantSKU) {
			variantSKU = productVariants.product!.variants.edges[0].node.sku?.value || null;
		}
	}
	if (!variantSKU) {
		error(500, 'No SKU found for product');
	}
	return variantSKU;
}
